<script setup lang="ts">
/**
 * A floating panel with what somebody building this application needs to see: the local
 * inboxes, the account this tab is acting as, and whatever the current route is carrying.
 *
 * It appears only when the backend says the deployment exposes it, and its text is
 * deliberately in English rather than in the catalogs — it is developer tooling, never
 * part of the product, and translating diagnostics into three languages would be ceremony.
 * `AGENTS.md` records that exception so nobody has to guess whether it was an oversight.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { DevelopmentLoginCode } from '@threadline/shared'
import { formatHandleForDisplay } from '@threadline/shared'
import { useRoute } from 'vue-router'
import { fetchRecentLoginCodes } from '../api/backendApiClient.ts'
import { resetApplicationState } from '../developmentTools/applicationReset.ts'
import { resetProgressiveWebApplication } from '../serviceWorker/pwaReset.ts'
import { useAuthenticationSession } from '../composables/useAuthenticationSession.ts'
import { useDevelopmentTools } from '../composables/useDevelopmentTools.ts'
import {
  clampPanelGeometry,
  persistPanelGeometry,
  resolveInitialPanelGeometry,
  type DevelopmentPanelGeometry,
} from '../composables/useDevelopmentToolsPanel.ts'

const currentRoute = useRoute()
const { developmentTools } = useDevelopmentTools()
const { authenticatedUser, deploymentLocale } = useAuthenticationSession()

function readViewportSize() {
  return { width: window.innerWidth, height: window.innerHeight }
}

const panelGeometry = ref<DevelopmentPanelGeometry>(resolveInitialPanelGeometry(readViewportSize()))

const panelStyle = computed(() => ({
  left: `${panelGeometry.value.left}px`,
  top: `${panelGeometry.value.top}px`,
  width: `${panelGeometry.value.width}px`,
  // A rolled up panel is exactly its header, so the height it will return to is kept.
  height: panelGeometry.value.isMinimized ? 'auto' : `${panelGeometry.value.height}px`,
}))

/** Route parameters are the per-page part: the account, the counterpart, the message. */
const routeParameters = computed(() =>
  Object.entries(currentRoute.params)
    .map(([parameterName, parameterValue]) => ({
      parameterName,
      parameterValue: Array.isArray(parameterValue) ? parameterValue.join('/') : parameterValue,
    }))
    .filter((routeParameter) => routeParameter.parameterValue.length > 0),
)

type PointerGesture = {
  startClientX: number
  startClientY: number
  /** Turns how far the pointer has travelled into the geometry that gesture produces. */
  apply: (horizontalDistance: number, verticalDistance: number) => DevelopmentPanelGeometry
}

let activeGesture: PointerGesture | null = null

function handlePointerMove(pointerEvent: MouseEvent): void {
  if (activeGesture === null) {
    return
  }
  panelGeometry.value = clampPanelGeometry(
    activeGesture.apply(
      pointerEvent.clientX - activeGesture.startClientX,
      pointerEvent.clientY - activeGesture.startClientY,
    ),
    readViewportSize(),
  )
}

function handlePointerUp(): void {
  if (activeGesture === null) {
    return
  }
  activeGesture = null
  window.removeEventListener('pointermove', handlePointerMove)
  window.removeEventListener('pointerup', handlePointerUp)
  persistPanelGeometry(panelGeometry.value)
}

function beginGesture(
  pointerEvent: MouseEvent,
  apply: PointerGesture['apply'],
): void {
  pointerEvent.preventDefault()
  activeGesture = { startClientX: pointerEvent.clientX, startClientY: pointerEvent.clientY, apply }
  window.addEventListener('pointermove', handlePointerMove)
  window.addEventListener('pointerup', handlePointerUp)
}

function beginDrag(pointerEvent: MouseEvent): void {
  const startGeometry = { ...panelGeometry.value }
  beginGesture(pointerEvent, (horizontalDistance, verticalDistance) => ({
    ...startGeometry,
    left: startGeometry.left + horizontalDistance,
    top: startGeometry.top + verticalDistance,
  }))
}

function beginResize(pointerEvent: MouseEvent): void {
  const startGeometry = { ...panelGeometry.value }
  beginGesture(pointerEvent, (horizontalDistance, verticalDistance) => ({
    ...startGeometry,
    width: startGeometry.width + horizontalDistance,
    height: startGeometry.height + verticalDistance,
  }))
}

/**
 * The codes this deployment just sent. Polled rather than pushed: it is a development
 * convenience, and a request every couple of seconds costs nothing next to the round trip
 * of opening an inbox by hand.
 */
const recentLoginCodes = ref<DevelopmentLoginCode[]>([])
const LOGIN_CODE_POLL_INTERVAL_MS = 2_000
let loginCodePollTimer: ReturnType<typeof setInterval> | null = null

async function refreshRecentLoginCodes(): Promise<void> {
  try {
    recentLoginCodes.value = (await fetchRecentLoginCodes()).loginCodes
  } catch {
    // The route does not exist where the tools are off, and a failed poll is never worth
    // interrupting somebody over.
    recentLoginCodes.value = []
  }
}

function stopPollingLoginCodes(): void {
  if (loginCodePollTimer !== null) {
    clearInterval(loginCodePollTimer)
    loginCodePollTimer = null
  }
}

/** Only polls while there is something to poll for, and while the panel is open. */
watch(
  [() => developmentTools.value !== null, () => panelGeometry.value.isMinimized],
  ([toolsAvailable, isMinimized]) => {
    stopPollingLoginCodes()
    if (!toolsAvailable || isMinimized) {
      return
    }
    void refreshRecentLoginCodes()
    loginCodePollTimer = setInterval(() => void refreshRecentLoginCodes(), LOGIN_CODE_POLL_INTERVAL_MS)
  },
  { immediate: true },
)

onMounted(() => {
  if (developmentTools.value !== null && !panelGeometry.value.isMinimized) {
    void refreshRecentLoginCodes()
  }
})

/** A phone number or address is long; the panel is narrow. */
function describeRecipient(loginCode: DevelopmentLoginCode): string {
  const shapedHandle = formatHandleForDisplay(loginCode.handle)
  return shapedHandle.length > 24 ? `${shapedHandle.slice(0, 23)}…` : shapedHandle
}

/**
 * The way out of a stale service worker, which is the trap this panel exists to shorten.
 * The function is called directly rather than through `window.pwaReset`, which only exists
 * in a development build — the panel also runs on a production build served locally.
 */
async function resetThisProgressiveWebApplication(): Promise<void> {
  await resetProgressiveWebApplication()
}

/**
 * The larger hammer: forget everything this origin kept, this device's signed-in accounts
 * included. Confirmed first, because it is one click away from the button beside it and
 * undoing it means signing every account in again.
 */
async function resetThisApplicationState(): Promise<void> {
  if (!window.confirm('Clear all storage, cookies and caches, and sign every account out of this device?')) {
    return
  }
  await resetApplicationState()
}

function toggleMinimized(): void {
  panelGeometry.value = { ...panelGeometry.value, isMinimized: !panelGeometry.value.isMinimized }
  persistPanelGeometry(panelGeometry.value)
}

onBeforeUnmount(() => {
  // A gesture in progress when the panel goes away must not keep the window listening.
  handlePointerUp()
  stopPollingLoginCodes()
})
</script>

<template>
  <aside
    v-if="developmentTools !== null"
    class="development-tools-panel"
    :style="panelStyle"
    data-testid="development-tools-panel"
    aria-label="Development tools"
  >
    <header class="development-tools-panel__header" @pointerdown="beginDrag">
      <span class="development-tools-panel__title">Dev tools</span>
      <span class="development-tools-panel__route">{{ String(currentRoute.name ?? '') }}</span>
      <button
        type="button"
        class="development-tools-panel__toggle"
        data-testid="development-tools-toggle"
        :aria-expanded="!panelGeometry.isMinimized"
        :title="panelGeometry.isMinimized ? 'Expand' : 'Minimize'"
        @pointerdown.stop
        @click="toggleMinimized"
      >
        {{ panelGeometry.isMinimized ? '▢' : '—' }}
      </button>
    </header>

    <div v-if="!panelGeometry.isMinimized" class="development-tools-panel__body">
      <section class="development-tools-panel__section">
        <h2>Local inboxes</h2>
        <a
          v-if="developmentTools.smsInboxUrl !== null"
          :href="developmentTools.smsInboxUrl"
          target="_blank"
          rel="noreferrer"
          data-testid="development-tools-sms-inbox"
        >
          Text messages (LocalStack)
        </a>
        <p v-else class="development-tools-panel__note">
          Text messages go to real AWS: no local inbox.
        </p>
        <a
          v-if="developmentTools.emailInboxUrl !== null"
          :href="developmentTools.emailInboxUrl"
          target="_blank"
          rel="noreferrer"
          data-testid="development-tools-email-inbox"
        >
          Email (Mailpit)
        </a>
      </section>

      <section class="development-tools-panel__section">
        <h2>Reset</h2>
        <button
          type="button"
          class="development-tools-panel__action"
          data-testid="development-tools-pwa-reset"
          title="Unregisters every service worker, empties the caches and reloads"
          @click="resetThisProgressiveWebApplication"
        >
          pwaReset()
        </button>
        <button
          type="button"
          class="development-tools-panel__action"
          data-testid="development-tools-app-reset"
          title="Clears storage, cookies, IndexedDB and caches, signs every account out of this device and reloads"
          @click="resetThisApplicationState"
        >
          appReset()
        </button>
      </section>

      <section class="development-tools-panel__section">
        <h2>Login codes</h2>
        <p v-if="recentLoginCodes.length === 0" class="development-tools-panel__note">
          Nothing sent yet.
        </p>
        <ul v-else class="development-tools-panel__codes" data-testid="development-tools-login-codes">
          <li v-for="(loginCode, position) in recentLoginCodes" :key="`${loginCode.issuedAt}-${position}`">
            <code v-if="loginCode.loginCode !== null" data-testid="development-tools-login-code">{{
              loginCode.loginCode
            }}</code>
            <a
              v-else-if="loginCode.loginUrl !== null"
              :href="loginCode.loginUrl"
              data-testid="development-tools-login-link"
            >
              open login link
            </a>
            <span class="development-tools-panel__recipient">{{ describeRecipient(loginCode) }}</span>
            <span class="development-tools-panel__note">{{ loginCode.purpose }}</span>
          </li>
        </ul>
      </section>

      <section class="development-tools-panel__section">
        <h2>This page</h2>
        <dl>
          <template v-for="routeParameter in routeParameters" :key="routeParameter.parameterName">
            <dt>{{ routeParameter.parameterName }}</dt>
            <dd>{{ routeParameter.parameterValue }}</dd>
          </template>
          <template v-if="routeParameters.length === 0">
            <dt>params</dt>
            <dd>none</dd>
          </template>
        </dl>
      </section>

      <section class="development-tools-panel__section">
        <h2>Account</h2>
        <dl>
          <dt>handle</dt>
          <dd>{{ authenticatedUser?.handle ?? 'not signed in' }}</dd>
          <template v-if="authenticatedUser !== null">
            <dt>account id</dt>
            <dd>{{ authenticatedUser.accountId }}</dd>
          </template>
          <dt>locale</dt>
          <dd>{{ deploymentLocale ?? 'intl' }}</dd>
        </dl>
      </section>
    </div>

    <button
      v-if="!panelGeometry.isMinimized"
      type="button"
      class="development-tools-panel__resize"
      data-testid="development-tools-resize"
      aria-label="Resize development tools"
      @pointerdown="beginResize"
    />
  </aside>
</template>

<style scoped>
/* Deliberately unlike the product: this must never be mistaken for part of it. */
.development-tools-panel {
  position: fixed;
  z-index: 2400;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #14161a;
  color: #e6e8eb;
  border: 1px solid #3a3f47;
  border-radius: 8px;
  box-shadow: 0 8px 28px rgb(0 0 0 / 45%);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.5;
}

.development-tools-panel__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: #22262c;
  cursor: move;
  touch-action: none;
  user-select: none;
}

.development-tools-panel__title {
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.development-tools-panel__route {
  flex: 1 1 auto;
  overflow: hidden;
  color: #9aa3ad;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.development-tools-panel__toggle {
  flex: 0 0 auto;
  padding: 0 6px;
  color: inherit;
  background: transparent;
  border: 1px solid #3a3f47;
  border-radius: 4px;
  cursor: pointer;
}

.development-tools-panel__body {
  flex: 1 1 auto;
  overflow: auto;
  padding: 8px;
}

.development-tools-panel__section + .development-tools-panel__section {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #2b3037;
}

.development-tools-panel__section h2 {
  margin: 0 0 4px;
  color: #9aa3ad;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.development-tools-panel__section a {
  display: block;
  overflow: hidden;
  color: #7fb2ff;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.development-tools-panel__action {
  /* Two of them now sit side by side rather than one filling the row. */
  margin-right: 6px;
  padding: 3px 8px;
  color: #ffc9c9;
  font-family: inherit;
  font-size: inherit;
  background: transparent;
  border: 1px solid #6b4046;
  border-radius: 4px;
  cursor: pointer;
}

.development-tools-panel__action:hover {
  background: #2c1c1f;
}

.development-tools-panel__codes {
  margin: 0;
  padding: 0;
  list-style: none;
}

.development-tools-panel__codes li {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.development-tools-panel__codes code {
  color: #9ae6a0;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.development-tools-panel__recipient {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.development-tools-panel__note {
  margin: 0;
  color: #9aa3ad;
}

.development-tools-panel__section dl {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0 8px;
  margin: 0;
}

.development-tools-panel__section dt {
  color: #9aa3ad;
}

.development-tools-panel__section dd {
  overflow-wrap: anywhere;
  margin: 0;
}

.development-tools-panel__resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 16px;
  height: 16px;
  padding: 0;
  background: linear-gradient(135deg, transparent 50%, #4a515a 50%);
  border: 0;
  cursor: nwse-resize;
  touch-action: none;
}
</style>
